import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const VesselsDataGrid = ({ searchQuery, onEdit, onDelete, selectedItems, onSelectionChange }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const vesselsData = [
    {
      id: 'VES001',
      name: 'Pacific Voyager',
      imo: 'IMO9876543',
      flag: 'Panama',
      vesselType: 'Bulk Carrier',
      dwt: 82000,
      loa: 229,
      beam: 32.26,
      draft: 14.5,
      buildYear: 2018,
      owner: 'Pacific Shipping Ltd',
      status: 'available',
      lastPort: 'Port of Newcastle',
      eta: '2025-12-15',
      syncStatus: 'synced'
    },
    {
      id: 'VES002',
      name: 'Ocean Harmony',
      imo: 'IMO9765432',
      flag: 'Liberia',
      vesselType: 'Bulk Carrier',
      dwt: 95000,
      loa: 245,
      beam: 43,
      draft: 16.2,
      buildYear: 2020,
      owner: 'Global Marine Corp',
      status: 'in-transit',
      lastPort: 'Port of Brisbane',
      eta: '2025-12-18',
      syncStatus: 'synced'
    },
    {
      id: 'VES003',
      name: 'Star Navigator',
      imo: 'IMO9654321',
      flag: 'Marshall Islands',
      vesselType: 'Bulk Carrier',
      dwt: 78000,
      loa: 225,
      beam: 32,
      draft: 14.0,
      buildYear: 2019,
      owner: 'Star Shipping Inc',
      status: 'loading',
      lastPort: 'Port of Gladstone',
      eta: '2025-12-20',
      syncStatus: 'pending'
    },
    {
      id: 'VES004',
      name: 'Horizon Trader',
      imo: 'IMO9543210',
      flag: 'Singapore',
      vesselType: 'Bulk Carrier',
      dwt: 88000,
      loa: 235,
      beam: 38,
      draft: 15.5,
      buildYear: 2021,
      owner: 'Horizon Maritime',
      status: 'available',
      lastPort: 'Port of Hay Point',
      eta: '2025-12-22',
      syncStatus: 'synced'
    },
    {
      id: 'VES005',
      name: 'Eastern Spirit',
      imo: 'IMO9432109',
      flag: 'Hong Kong',
      vesselType: 'Bulk Carrier',
      dwt: 92000,
      loa: 240,
      beam: 42,
      draft: 16.0,
      buildYear: 2017,
      owner: 'Eastern Shipping Co',
      status: 'maintenance',
      lastPort: 'Mundra Port',
      eta: '2026-01-05',
      syncStatus: 'error'
    }
  ];

  const filteredData = vesselsData?.filter(vessel =>
    vessel?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    vessel?.imo?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    vessel?.owner?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedData = [...filteredData]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(sortedData?.map(item => item?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      onSelectionChange([...selectedItems, id]);
    } else {
      onSelectionChange(selectedItems?.filter(itemId => itemId !== id));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-success/10 text-success';
      case 'in-transit': return 'bg-primary/10 text-primary';
      case 'loading': return 'bg-warning/10 text-warning';
      case 'maintenance': return 'bg-error/10 text-error';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getSyncStatusColor = (status) => {
    switch (status) {
      case 'synced': return 'text-success';
      case 'pending': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-muted/50 border-b border-border">
          <tr>
            <th className="px-4 py-3 text-left">
              <Checkbox
                checked={selectedItems?.length === sortedData?.length && sortedData?.length > 0}
                onChange={(e) => handleSelectAll(e?.target?.checked)}
              />
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('imo')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                IMO
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('name')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Vessel Name
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Type & Flag</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('dwt')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Specifications
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Owner</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Current Location</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sync</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sortedData?.map((vessel) => (
            <tr key={vessel?.id} className="hover:bg-muted/30 transition-colors">
              <td className="px-4 py-3">
                <Checkbox
                  checked={selectedItems?.includes(vessel?.id)}
                  onChange={(e) => handleSelectItem(vessel?.id, e?.target?.checked)}
                />
              </td>
              <td className="px-4 py-3">
                <span className="font-mono text-sm font-medium">{vessel?.imo}</span>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="font-medium text-foreground">{vessel?.name}</span>
                  <span className="text-xs text-muted-foreground">Built {vessel?.buildYear}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col gap-1">
                  <span className="text-sm">{vessel?.vesselType}</span>
                  <span className="text-xs text-muted-foreground">{vessel?.flag}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs"><span className="font-medium">DWT:</span> {vessel?.dwt?.toLocaleString()} MT</span>
                  <span className="text-xs"><span className="font-medium">LOA:</span> {vessel?.loa}m × {vessel?.beam}m</span>
                  <span className="text-xs"><span className="font-medium">Draft:</span> {vessel?.draft}m</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm">{vessel?.owner}</span>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="text-sm">{vessel?.lastPort}</span>
                  <span className="text-xs text-muted-foreground">ETA: {vessel?.eta}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(vessel?.status)}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    vessel?.status === 'available' ? 'bg-success' :
                    vessel?.status === 'in-transit' ? 'bg-primary' :
                    vessel?.status === 'loading' ? 'bg-warning' : 'bg-error'
                  }`} />
                  {vessel?.status}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <Icon
                    name={vessel?.syncStatus === 'synced' ? 'CheckCircle2' : vessel?.syncStatus === 'pending' ? 'Clock' : 'AlertCircle'}
                    size={16}
                    className={getSyncStatusColor(vessel?.syncStatus)}
                  />
                  <span className={`text-xs ${getSyncStatusColor(vessel?.syncStatus)}`}>
                    {vessel?.syncStatus}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Edit"
                    onClick={() => onEdit(vessel)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => onDelete(vessel?.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sortedData?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="Search" size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No vessels found matching your search</p>
        </div>
      )}
    </div>
  );
};

export default VesselsDataGrid;